import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Music, Play, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#C8A840';
const ALBUM_ART = 'https://static.prod-images.emergentagent.com/jobs/28b9aa9a-e939-49d7-ba3a-65ce742f1b33/images/6a369e83b56f44cf6159d117ff8d0c37d26abf5f8fa6a7577d320e3c0b2a6f05.png';

const TRACKS = [
  { num: '01', title: 'Before Dawn', style: 'Ambient \u00b7 Spoken Word', duration: '2:10', description: 'The film\u2019s first breath. One piano note. A voice over silence before the title card.', mood: 'Before anything is named.', color: '#3a3020' },
  { num: '02', title: 'The Road Had No Name', style: 'Piano Ballad \u00b7 First Person', duration: '3:45', description: 'Her voice, her road, her choice to go back. Sparse piano, first person.', mood: 'Tracy Chapman meets Erykah Badu.', color: '#2a2518' },
  { num: '03', title: 'Zero', style: 'Spoken Word \u00b7 R&B', duration: '4:12', description: 'Fifty years. Eight billion dollars. Zero proven programmes. The coldest track.', mood: 'Common meets Sade.', color: '#1a1a22' },
  { num: '04', title: 'What She Carried', style: 'Gospel Soul \u00b7 Choir', duration: '4:58', description: 'The folder. Receipts filed for no one. One voice to full choir.', mood: 'Jennifer Hudson meets Nina Simone.', color: '#2a1f18' },
  { num: '05', title: 'Still Here', style: 'World Music \u00b7 5 Languages', duration: '5:20', description: 'Five singers, five languages, five regions. Oud, kora, piano. The most globally powerful track.', mood: 'Arabic, Amharic, Hindi, French, English.', color: '#1a2520' },
  { num: '06', title: 'She Built This', style: 'Anthem \u00b7 Full Orchestra', duration: '5:47', description: 'Sparse piano to full orchestra. Three seconds of silence before \u201cYes. I would.\u201d', mood: 'The primary submission.', color: '#2a2210', isMain: true },
  { num: '07', title: 'I\u2019ll Do It Again', style: 'Voice Only \u00b7 Prophetic', duration: '4:03', description: 'A final address to the future. Voice alone. Past tense becomes future tense.', mood: 'Prophecy.', color: '#1a1a1a' },
];

const GRAMMY_CATS = [
  { cat: 'Best Song Written for Visual Media', track: 'Track 06', primary: true },
  { cat: 'Best Score Soundtrack for Visual Media', track: 'Full Album' },
  { cat: 'Best Global Music Performance', track: 'Track 05' },
  { cat: 'Best Gospel Performance', track: 'Track 04' },
  { cat: 'Best Spoken Word Album', track: 'Tracks 01 + 03' },
];

/* ═══ AUDIO VISUALIZER — Canvas bars ═══ */
const AudioVisualizer = () => {
  const canvasRef = useRef(null);
  const barsRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = 600;
    const H = canvas.height = 120;
    const BAR_COUNT = 64;
    const BAR_W = W / BAR_COUNT - 1;

    barsRef.current = Array.from({ length: BAR_COUNT }, (_, i) => ({
      h: Math.random() * 20 + 5,
      target: Math.random() * 60 + 10,
      speed: Math.random() * 0.03 + 0.02,
      phase: i * 0.15,
    }));

    const draw = (ts) => {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < BAR_COUNT; i++) {
        const bar = barsRef.current[i];
        bar.target = 10 + Math.sin(ts * 0.001 + bar.phase) * 25 + Math.sin(ts * 0.0023 + bar.phase * 1.7) * 20 + Math.random() * 8;
        bar.h += (bar.target - bar.h) * bar.speed;
        const x = i * (BAR_W + 1);
        const h = Math.max(bar.h, 2);
        const gradient = ctx.createLinearGradient(x, H, x, H - h);
        gradient.addColorStop(0, 'rgba(200, 168, 64, 0.05)');
        gradient.addColorStop(0.5, 'rgba(200, 168, 64, 0.25)');
        gradient.addColorStop(1, 'rgba(200, 168, 64, 0.6)');
        ctx.fillStyle = gradient;
        ctx.fillRect(x, H - h, BAR_W, h);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw(0);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  return <canvas ref={canvasRef} className="w-full max-w-[600px] h-[120px] opacity-60" />;
};

/* ═══ WAVEFORM BAR for each track ═══ */
const TrackWaveform = ({ duration }) => {
  const mins = parseInt(duration.split(':')[0]);
  const secs = parseInt(duration.split(':')[1]);
  const totalSecs = mins * 60 + secs;
  const barCount = Math.floor(totalSecs / 3);
  return (
    <div className="flex items-end gap-[1px] h-6">
      {Array.from({ length: barCount }, (_, i) => {
        const h = 4 + Math.sin(i * 0.7) * 10 + Math.random() * 8;
        return <div key={i} className="w-[2px] rounded-full" style={{ height: `${Math.max(h, 3)}px`, background: `rgba(200, 168, 64, ${0.15 + Math.random() * 0.25})` }} />;
      })}
    </div>
  );
};

const SoundtrackPage = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [expandedTrack, setExpandedTrack] = useState(null);
  const [hoveredTrack, setHoveredTrack] = useState(null);
  const [playingTrack, setPlayingTrack] = useState(null);
  const audioCtxRef = useRef(null);
  const activeNodesRef = useRef([]);

  const getAudioCtx = useCallback(() => {
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  }, []);

  const stopAllAudio = useCallback(() => {
    activeNodesRef.current.forEach(node => {
      try { node.stop?.(); node.disconnect?.(); } catch {}
    });
    activeNodesRef.current = [];
    setPlayingTrack(null);
  }, []);

  /* ═══ CINEMATIC AUDIO ENGINE ═══ */
  const playTrackPreview = useCallback((trackIndex) => {
    if (playingTrack === trackIndex) { stopAllAudio(); return; }
    stopAllAudio();

    const ctx = getAudioCtx();
    const now = ctx.currentTime;
    const nodes = [];
    const DUR = 10;

    /* Master bus: compressor → reverb → output */
    const masterGain = ctx.createGain();
    masterGain.gain.value = 0.7;
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -24;
    compressor.ratio.value = 4;
    compressor.attack.value = 0.003;
    compressor.release.value = 0.25;

    /* Convolution-free reverb: multi-tap delay feedback */
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = 0.35;
    const delays = [0.03, 0.07, 0.13, 0.21, 0.34, 0.55].map(t => {
      const d = ctx.createDelay(1); d.delayTime.value = t;
      const g = ctx.createGain(); g.gain.value = 0.15 / (t * 4 + 1);
      const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = 2000 - t * 1500;
      d.connect(f).connect(g).connect(reverbGain);
      return d;
    });
    const reverbInput = ctx.createGain();
    reverbInput.gain.value = 1;
    delays.forEach(d => reverbInput.connect(d));
    reverbGain.connect(masterGain);
    reverbInput.connect(masterGain);
    masterGain.connect(compressor).connect(ctx.destination);
    nodes.push(masterGain, compressor, reverbGain, reverbInput, ...delays);

    /* Warm piano-like tone with harmonics + decay */
    const piano = (freq, time, dur, vel = 0.12) => {
      const harmonics = [1, 2, 3, 4, 5, 6];
      const amps = [1, 0.5, 0.25, 0.12, 0.06, 0.03];
      harmonics.forEach((h, hi) => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq * h;
        /* Slight detune for warmth */
        osc.detune.value = (Math.random() - 0.5) * 6;
        const g = ctx.createGain();
        const amp = vel * amps[hi];
        const t = now + time;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(amp, t + 0.008);
        g.gain.exponentialRampToValueAtTime(amp * 0.5, t + 0.1);
        g.gain.exponentialRampToValueAtTime(0.001, t + dur);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 3000 - hi * 300;
        lp.Q.value = 0.7;
        osc.connect(lp).connect(g).connect(reverbInput);
        osc.start(t);
        osc.stop(t + dur + 0.05);
        nodes.push(osc, g, lp);
      });
    };

    /* Pad: detuned oscillators with slow attack for atmosphere */
    const pad = (freq, time, dur, vol = 0.04) => {
      [-4, -1, 0, 1, 4].forEach(detune => {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;
        osc.detune.value = detune;
        const g = ctx.createGain();
        const t = now + time;
        g.gain.setValueAtTime(0, t);
        g.gain.linearRampToValueAtTime(vol, t + dur * 0.4);
        g.gain.linearRampToValueAtTime(vol * 0.8, t + dur * 0.7);
        g.gain.linearRampToValueAtTime(0, t + dur);
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.value = 800;
        osc.connect(lp).connect(g).connect(reverbInput);
        osc.start(t);
        osc.stop(t + dur + 0.1);
        nodes.push(osc, g, lp);
      });
    };

    /* Sub bass: deep, warm foundation */
    const bass = (freq, time, dur, vol = 0.08) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const g = ctx.createGain();
      const t = now + time;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.3);
      g.gain.setValueAtTime(vol, t + dur - 0.5);
      g.gain.linearRampToValueAtTime(0, t + dur);
      osc.connect(g).connect(reverbInput);
      osc.start(t);
      osc.stop(t + dur + 0.1);
      nodes.push(osc, g);
    };

    /* Breath/texture: filtered noise */
    const breath = (time, dur, vol = 0.015) => {
      const bufSize = ctx.sampleRate * dur;
      const buf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
      const data = buf.getChannelData(0);
      for (let i = 0; i < bufSize; i++) data[i] = (Math.random() * 2 - 1);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 400;
      bp.Q.value = 2;
      const g = ctx.createGain();
      const t = now + time;
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(vol, t + dur * 0.3);
      g.gain.linearRampToValueAtTime(0, t + dur);
      src.connect(bp).connect(g).connect(reverbInput);
      src.start(t);
      src.stop(t + dur + 0.1);
      nodes.push(src, bp, g);
    };

    switch (trackIndex) {
      case 0: { // Before Dawn — one piano note in empty space, breath
        breath(0, DUR, 0.01);
        bass(65.41, 0, DUR, 0.03);
        piano(261.63, 1.5, 4, 0.14);
        pad(130.81, 0, DUR, 0.02);
        piano(523.25, 4, 3, 0.05);
        piano(392.00, 7, 3, 0.08);
        break;
      }
      case 1: { // The Road Had No Name — piano ballad, melancholy progression
        bass(65.41, 0, DUR, 0.05);
        pad(130.81, 0, DUR, 0.025);
        // C minor → Eb → Ab → G
        const prog1 = [[261.63, 311.13, 392.00], [311.13, 392.00, 466.16], [415.30, 523.25, 622.25], [392.00, 493.88, 587.33]];
        prog1.forEach((chord, i) => {
          chord.forEach(f => piano(f, i * 2.2 + 0.5, 2.5, 0.09));
        });
        breath(0, 4, 0.008);
        break;
      }
      case 2: { // Zero — cold, sparse, tension
        bass(55, 0, DUR, 0.07);
        breath(0, DUR, 0.02);
        // Dissonant stabs
        [1.5, 3.5, 6, 8].forEach(t => {
          piano(233.08, t, 0.8, 0.1); // Bb
          piano(277.18, t, 0.8, 0.06); // C#
        });
        pad(110, 0, DUR, 0.03);
        // Ticking rhythm
        for (let t = 0; t < DUR; t += 0.75) {
          const click = ctx.createOscillator();
          click.type = 'sine';
          click.frequency.value = 3500;
          const cg = ctx.createGain();
          cg.gain.setValueAtTime(0, now + t);
          cg.gain.linearRampToValueAtTime(0.03, now + t + 0.002);
          cg.gain.exponentialRampToValueAtTime(0.001, now + t + 0.05);
          click.connect(cg).connect(reverbInput);
          click.start(now + t);
          click.stop(now + t + 0.06);
          nodes.push(click, cg);
        }
        break;
      }
      case 3: { // What She Carried — warm gospel chords, building
        bass(65.41, 0, DUR, 0.05);
        // Cmaj → Am → F → G progression, warm
        const prog3 = [
          [261.63, 329.63, 392.00],
          [220.00, 261.63, 329.63],
          [174.61, 220.00, 261.63],
          [196.00, 246.94, 293.66],
        ];
        prog3.forEach((chord, i) => {
          pad(chord[0] / 2, i * 2.3, 3, 0.03);
          chord.forEach(f => piano(f, i * 2.3 + 0.2, 2.8, 0.08));
        });
        // High note bloom
        pad(523.25, 5, 5, 0.025);
        pad(659.25, 6, 4, 0.02);
        break;
      }
      case 4: { // Still Here — world scale, pentatonic, oud-like
        bass(73.42, 0, DUR, 0.04);
        breath(0, DUR, 0.012);
        // D pentatonic minor: D Eb G A Bb
        const scale = [293.66, 311.13, 392.00, 440.00, 466.16, 587.33, 622.25, 783.99];
        scale.forEach((f, i) => {
          piano(f, i * 1.1 + 0.5, 2, 0.1);
        });
        pad(146.83, 0, DUR, 0.03);
        pad(293.66, 3, 7, 0.025);
        // Drone
        const drone = ctx.createOscillator();
        drone.type = 'sawtooth';
        drone.frequency.value = 146.83;
        const dLp = ctx.createBiquadFilter();
        dLp.type = 'lowpass'; dLp.frequency.value = 300;
        const dg = ctx.createGain();
        dg.gain.setValueAtTime(0, now);
        dg.gain.linearRampToValueAtTime(0.025, now + 2);
        dg.gain.setValueAtTime(0.025, now + DUR - 2);
        dg.gain.linearRampToValueAtTime(0, now + DUR);
        drone.connect(dLp).connect(dg).connect(reverbInput);
        drone.start(now); drone.stop(now + DUR + 0.1);
        nodes.push(drone, dLp, dg);
        break;
      }
      case 5: { // She Built This — the anthem, building from nothing to everything
        breath(0, 3, 0.008);
        // Phase 1: single piano note (0-3s)
        piano(261.63, 0.5, 3, 0.12);
        bass(65.41, 0, DUR, 0.04);
        // Phase 2: chord enters (3-6s)
        [261.63, 329.63, 392.00].forEach(f => piano(f, 3, 3.5, 0.08));
        pad(130.81, 2.5, 7.5, 0.02);
        pad(196.00, 3, 7, 0.02);
        // Phase 3: full swell (6-9s)
        [261.63, 329.63, 392.00, 523.25].forEach(f => pad(f, 5.5, 4.5, 0.03));
        piano(523.25, 6, 2, 0.1);
        piano(659.25, 7, 2, 0.08);
        piano(783.99, 8, 2, 0.12);
        // Crescendo strings
        [261.63, 329.63, 392.00, 523.25, 659.25].forEach(f => {
          const o = ctx.createOscillator();
          o.type = 'sawtooth'; o.frequency.value = f;
          const lp = ctx.createBiquadFilter();
          lp.type = 'lowpass'; lp.frequency.setValueAtTime(200, now + 6);
          lp.frequency.linearRampToValueAtTime(2000, now + 9);
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, now + 5);
          g.gain.linearRampToValueAtTime(0.02, now + 8.5);
          g.gain.linearRampToValueAtTime(0, now + DUR);
          o.connect(lp).connect(g).connect(reverbInput);
          o.start(now + 5); o.stop(now + DUR + 0.1);
          nodes.push(o, lp, g);
        });
        break;
      }
      case 6: { // I'll Do It Again — lone voice, intimate, prophetic
        breath(0, DUR, 0.01);
        // Single voice-like tone: vibrato sine
        const voice = ctx.createOscillator();
        voice.type = 'sine';
        voice.frequency.value = 220;
        const vibLfo = ctx.createOscillator();
        vibLfo.type = 'sine';
        vibLfo.frequency.value = 5;
        const vibGain = ctx.createGain();
        vibGain.gain.value = 3;
        vibLfo.connect(vibGain).connect(voice.frequency);
        const vg = ctx.createGain();
        vg.gain.setValueAtTime(0, now);
        vg.gain.linearRampToValueAtTime(0.12, now + 1.5);
        vg.gain.setValueAtTime(0.12, now + DUR - 3);
        vg.gain.linearRampToValueAtTime(0, now + DUR);
        const vLp = ctx.createBiquadFilter();
        vLp.type = 'lowpass'; vLp.frequency.value = 1200;
        voice.connect(vLp).connect(vg).connect(reverbInput);
        voice.start(now); voice.stop(now + DUR + 0.1);
        vibLfo.start(now); vibLfo.stop(now + DUR + 0.1);
        nodes.push(voice, vibLfo, vibGain, vg, vLp);
        // Second voice harmony enters late
        pad(220, 0, DUR, 0.02);
        pad(330, 5, 5, 0.015);
        bass(55, 0, DUR, 0.025);
        break;
      }
      default: break;
    }

    activeNodesRef.current = nodes;
    setPlayingTrack(trackIndex);

    setTimeout(() => {
      setPlayingTrack(prev => prev === trackIndex ? null : prev);
    }, DUR * 1000 + 500);
  }, [playingTrack, stopAllAudio, getAudioCtx]);

  useEffect(() => { return () => stopAllAudio(); }, [stopAllAudio]);

  const handleNotify = useCallback(async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try { await axios.post(`${API_URL}/api/newsletter/subscribe`, { email }); } catch {}
    setSubscribed(true);
    setSubmitting(false);
  }, [email]);

  return (
    <div data-testid="soundtrack-page" className="min-h-screen bg-[#08080C]">
      <SEO title="The Soundtrack | She Built This" description="Seven original tracks. Five Grammy categories. The sound of women who built something that was never supposed to exist." />

      {/* ═══════════════════════════════════════════
          HERO — Album art + Visualizer
          ═══════════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 film-grain-overlay opacity-25 pointer-events-none z-10" />
        <div className="absolute inset-0 z-5 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(200,168,64,0.04) 0%, transparent 50%, rgba(0,0,0,0.8) 100%)' }} />

        <div className="relative z-20 max-w-5xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-16 lg:gap-20 items-center">
            {/* Album Art */}
            <div className="relative flex justify-center">
              {/* Glow */}
              <div className="absolute inset-0 blur-3xl opacity-30" style={{ background: `radial-gradient(circle, ${GOLD} 0%, transparent 60%)`, transform: 'scale(0.8)' }} />
              {/* Spinning ring behind */}
              <div className="absolute inset-[-30px] rounded-full border border-white/[0.03] animate-spin" style={{ animationDuration: '40s' }}>
                <div className="absolute inset-[15px] rounded-full border border-white/[0.03]" />
              </div>
              <img
                src={ALBUM_ART}
                alt="She Built This — Album Art"
                data-testid="album-art"
                className="relative z-10 w-[280px] md:w-[340px] lg:w-[380px] h-auto"
                style={{ filter: 'drop-shadow(0 0 60px rgba(200,168,64,0.3))', animation: 'albumPulse 4s ease-in-out infinite alternate' }}
              />
            </div>

            {/* Info */}
            <div>
              <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 border border-[#1A1A1A]/10" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.3em', color: GOLD }}>
                <Music size={10} />
                ORIGINAL SOUNDTRACK
              </div>

              <h1 className="mb-2" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(44px, 6vw, 80px)', fontWeight: 700, letterSpacing: '0.04em', lineHeight: 1, color: '#fff' }}>
                SHE BUILT<br />THIS
              </h1>
              <p className="mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 300, fontStyle: 'italic', color: 'rgba(255,255,255,0.3)' }}>
                The Album
              </p>

              <div className="w-12 h-[1px] mb-6" style={{ background: `linear-gradient(to right, ${GOLD}, transparent)` }} />

              <p className="text-lg italic leading-relaxed mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.4)' }}>
                The world did not notice.
              </p>
              <p className="text-lg italic leading-relaxed mb-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: GOLD }}>
                This album makes the world notice.
              </p>

              <div className="flex items-center gap-6 mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.2)' }}>
                <span><Clock size={10} className="inline mr-1 mb-[1px]" /> 30:15</span>
                <span>7 TRACKS</span>
                <span style={{ color: GOLD }}>5 CATEGORIES</span>
              </div>

              {/* Mini visualizer */}
              <AudioVisualizer />

              <p className="mt-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.1)' }}>
                SHE BUILT THIS PRODUCTIONS &middot; VISIONMOVIE
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          TRACKLIST — Visual waveforms
          ═══════════════════════════════════════════ */}
      <section className="relative py-20 lg:py-28" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-3xl mx-auto px-6">
          <p className="mb-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
            TRACKLIST
          </p>
          <p className="mb-14" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.12)' }}>
            7 TRACKS &middot; 30:15 &middot; ORIGINAL SOUNDTRACK
          </p>

          <div className="space-y-0">
            {TRACKS.map((track, i) => (
              <div
                key={i}
                data-testid={`track-${track.num}`}
                className="group cursor-pointer transition-all duration-500"
                style={{
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                  background: hoveredTrack === i ? track.color : 'transparent',
                }}
                onClick={() => setExpandedTrack(expandedTrack === i ? null : i)}
                onMouseEnter={() => setHoveredTrack(i)}
                onMouseLeave={() => setHoveredTrack(null)}
              >
                <div className="flex items-center justify-between py-5 px-3">
                  <div className="flex items-center gap-5 flex-1 min-w-0">
                    <span className="w-6 text-right flex-shrink-0" style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: track.isMain ? GOLD : 'rgba(255,255,255,0.15)' }}>
                      {track.num}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); playTrackPreview(i); }}
                      data-testid={`play-btn-${track.num}`}
                      className={`w-10 h-10 rounded-full border flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                        playingTrack === i 
                          ? 'border-[rgba(200,168,64,0.8)] bg-[rgba(200,168,64,0.15)] scale-110' 
                          : track.isMain 
                            ? 'border-[rgba(200,168,64,0.5)] bg-[rgba(200,168,64,0.08)]' 
                            : 'border-white/8 group-hover:border-[rgba(200,168,64,0.3)]'
                      }`}
                    >
                      {playingTrack === i ? (
                        <div className="flex items-center gap-[3px]">
                          <div className="w-[3px] h-3 rounded-full animate-pulse" style={{ background: GOLD }} />
                          <div className="w-[3px] h-4 rounded-full animate-pulse" style={{ background: GOLD, animationDelay: '0.15s' }} />
                          <div className="w-[3px] h-2.5 rounded-full animate-pulse" style={{ background: GOLD, animationDelay: '0.3s' }} />
                        </div>
                      ) : (
                        <Play size={12} className="ml-[2px]" style={{ color: track.isMain ? GOLD : 'rgba(255,255,255,0.2)' }} />
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="block truncate" style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', fontWeight: track.isMain ? 600 : 400, color: track.isMain ? '#fff' : 'rgba(255,255,255,0.7)' }}>
                          {track.title}
                        </span>
                        {track.isMain && <span className="text-[8px] tracking-[0.15em] px-2 py-[2px] border flex-shrink-0" style={{ fontFamily: "'DM Mono', monospace", color: GOLD, borderColor: 'rgba(200,168,64,0.4)', background: 'rgba(200,168,64,0.05)' }}>MAIN</span>}
                      </div>
                      <span className="block mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.12em', color: 'rgba(255,255,255,0.15)' }}>
                        {track.style}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="hidden md:block">
                      <TrackWaveform duration={track.duration} />
                    </div>
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', color: 'rgba(255,255,255,0.15)' }}>
                      {track.duration}
                    </span>
                  </div>
                </div>

                {expandedTrack === i && (
                  <div className="pb-6 pl-[76px] pr-6" style={{ animation: 'fadeSlideDown 0.3s ease-out' }}>
                    <p className="mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: 300, color: 'rgba(255,255,255,0.45)', lineHeight: 1.7 }}>
                      {track.description}
                    </p>
                    <p className="italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '14px', color: GOLD, opacity: 0.5 }}>
                      {track.mood}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <p className="text-center mt-8" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.1)' }}>
            SYNTHESIZED PREVIEWS &middot; FULL RECORDINGS COMING SOON
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          THE ANTHEM — Track 06 cinematic spotlight
          ═══════════════════════════════════════════ */}
      <section className="relative py-0 overflow-hidden" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="absolute inset-0 film-grain-overlay opacity-15 pointer-events-none z-10" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(200,168,64,0.03) 0%, transparent 60%)' }} />

        <div className="relative z-20 max-w-3xl mx-auto px-6 py-28 lg:py-36 text-center">
          <p className="mb-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.35em', color: 'rgba(255,255,255,0.15)' }}>
            TRACK 06 &middot; 5:47
          </p>
          <h2 className="mb-2" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(44px, 7vw, 84px)', fontWeight: 700, color: '#fff', lineHeight: 1, letterSpacing: '0.04em' }}>
            She Built This
          </h2>
          <p className="mb-12" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: GOLD }}>
            THE ANTHEM &middot; FULL ORCHESTRA
          </p>

          <div className="space-y-4 mb-16" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.8 }}>
            <p>Begins with sparse piano. One voice.</p>
            <p>Builds to full orchestra.</p>
            <p style={{ color: '#fff' }}>Shifts from <em>&ldquo;she built this&rdquo;</em> to <em>&ldquo;I built this.&rdquo;</em></p>
          </div>

          {/* THE GRAMMY MOMENT */}
          <div className="py-16">
            <div className="w-32 h-[1px] mx-auto mb-12" style={{ background: 'rgba(200,168,64,0.1)' }} />
            <p className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.3em', color: 'rgba(255,255,255,0.1)' }}>
              THREE SECONDS OF SILENCE
            </p>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-8 h-[1px]" style={{ background: 'rgba(200,168,64,0.2)' }} />
              <div className="w-2 h-2 rounded-full" style={{ background: GOLD, boxShadow: `0 0 20px ${GOLD}` }} />
              <div className="w-8 h-[1px]" style={{ background: 'rgba(200,168,64,0.2)' }} />
            </div>
            <p style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(48px, 8vw, 96px)', fontWeight: 700, fontStyle: 'italic', color: GOLD, lineHeight: 1.1, textShadow: `0 0 80px rgba(200,168,64,0.3), 0 0 160px rgba(200,168,64,0.1)` }}>
              &ldquo;Yes. I would.&rdquo;
            </p>
            <div className="w-32 h-[1px] mx-auto mt-12" style={{ background: 'rgba(200,168,64,0.1)' }} />
          </div>

          <p className="mt-8" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 300, color: 'rgba(255,255,255,0.25)' }}>
            Then the final chorus erupts from silence.<br />
            Three hundred names scrolling slowly on black.
          </p>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          GRAMMY STRATEGY
          ═══════════════════════════════════════════ */}
      <section className="py-20 lg:py-28" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-2xl mx-auto px-6">
          <p className="text-center mb-3" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            FIVE GRAMMY CATEGORIES
          </p>
          <h3 className="text-center mb-14" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            One album. Five submissions.
          </h3>

          {GRAMMY_CATS.map((gc, i) => (
            <div key={i} className="flex items-center justify-between py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full" style={{ background: gc.primary ? GOLD : 'rgba(255,255,255,0.08)' }} />
                <span style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '16px', fontWeight: gc.primary ? 500 : 300, color: gc.primary ? '#fff' : 'rgba(255,255,255,0.4)' }}>
                  {gc.cat}
                </span>
              </div>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.1em', color: gc.primary ? GOLD : 'rgba(255,255,255,0.12)' }}>
                {gc.track}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          QUOTE
          ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-32 relative" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="absolute inset-0 film-grain-overlay opacity-10 pointer-events-none" />
        <div className="max-w-2xl mx-auto px-6 text-center relative z-10">
          <blockquote className="italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 300, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
            &ldquo;Three hundred women built schools, roads, systems, and communities in some of the most difficult places on earth.
            The world did not notice. This song makes the world notice.&rdquo;
          </blockquote>
          <p className="mt-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.1)' }}>
            THAT IS NOT A STRATEGY. THAT IS WHY THE RECOGNITION WILL FOLLOW.
          </p>

          <div className="grid grid-cols-3 gap-8 mt-16">
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: GOLD }}>5</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.12)' }}>LANGUAGES</p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: '#fff' }}>300+</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.12)' }}>WOMEN</p>
            </div>
            <div>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '32px', fontWeight: 700, color: GOLD }}>1</p>
              <p className="mt-1" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.12)' }}>ANTHEM</p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          NOTIFY ME
          ═══════════════════════════════════════════ */}
      <section className="py-24 lg:py-28" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-xl mx-auto px-6 text-center">
          <p className="mb-5" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            BE THE FIRST TO HEAR IT
          </p>
          <h3 className="mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(28px, 4vw, 40px)', fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
            Get notified when<br />the album drops.
          </h3>
          <p className="mb-10" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '17px', fontWeight: 300, color: 'rgba(255,255,255,0.25)' }}>
            No spam. Just the music.
          </p>

          {subscribed ? (
            <div data-testid="soundtrack-subscribed" className="py-4 px-6 border" style={{ borderColor: GOLD }}>
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '12px', letterSpacing: '0.15em', color: GOLD }}>YOU'RE ON THE LIST.</p>
            </div>
          ) : (
            <form onSubmit={handleNotify} className="flex flex-col sm:flex-row gap-3" data-testid="soundtrack-notify-form">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required data-testid="soundtrack-email-input"
                className="flex-1 h-14 px-6 bg-transparent border border-[#1A1A1A]/10 text-white placeholder:text-white/15 outline-none focus:border-[rgba(200,168,64,0.4)] transition-colors"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: '13px', letterSpacing: '0.05em' }} />
              <button type="submit" disabled={submitting} data-testid="soundtrack-notify-btn"
                className="h-14 px-10 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)] disabled:opacity-50"
                style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}>
                {submitting ? '...' : 'NOTIFY ME'}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* ═══════════════════════════════════════════
          CROSS-LINKS
          ═══════════════════════════════════════════ */}
      <section className="py-14" style={{ borderTop: '1px solid rgba(200,168,64,0.08)' }}>
        <div className="max-w-3xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            {[
              { to: '/film', label: 'THE FILM', sub: 'Watch the Teaser' },
              { to: '/book', label: 'THE BOOK', sub: 'Read the Prologue' },
              { to: '/profiles', label: 'THE PROFILES', sub: 'Explore 369+ Stories' },
            ].map(link => (
              <Link key={link.to} to={link.to} className="group py-5 border border-[#1A1A1A]/10 transition-all duration-300 hover:border-[rgba(200,168,64,0.2)]">
                <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.15)' }}>{link.label}</p>
                <p className="mt-1 group-hover:text-[#C8A840] transition-colors" style={{ fontFamily: "'Playfair Display', serif", fontSize: '16px', color: 'rgba(255,255,255,0.4)' }}>{link.sub}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes albumPulse {
          0% { filter: drop-shadow(0 0 40px rgba(200,168,64,0.2)); }
          100% { filter: drop-shadow(0 0 70px rgba(200,168,64,0.35)); }
        }
      `}</style>
    </div>
  );
};

export default SoundtrackPage;
